import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Layout } from "../components/Layout";
import { DateCard } from "../components/DateCard";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { useJsonLd } from "../lib/useJsonLd";
import { categoryPageJsonLd } from "../lib/jsonLd";
import { CATEGORY_LABELS, sortByDate } from "../lib/format";
import { fetchDates } from "../lib/api";

const BASE = "https://willnotbeforgotten.org";

const CATEGORY_DESCRIPTIONS = {
  "black-history": "Holidays, anniversaries, and milestones in Black American history — from emancipation and civil rights to the people and movements that reshaped the nation.",
  "civil-rights": "Key dates in the American civil rights struggle: landmark legislation, protests, court rulings, and the organizers who fought for equal protection under the law.",
  indigenous: "Observances and historic milestones in Native American and Indigenous history — sovereignty, survival, resistance, and cultural recognition.",
  women: "Milestones in U.S. women's history: suffrage, labor, reproductive rights, and the women who changed American life.",
  lgbtq: "LGBTQ+ history in the United States — Stonewall and beyond: pride, protest, legal victories, and the long fight for recognition and equality.",
  labor: "The American labor movement in dates: strikes, safety laws, union milestones, and the workers who built the modern workplace.",
  immigrant: "Immigrant history and the milestones that shaped the United States — arrivals, exclusion, naturalization, and contribution.",
  disability: "Disability justice milestones: the ADA, accessibility victories, and the activists who fought for dignity and inclusion.",
  democracy: "Democracy and voting rights in America — the laws, court decisions, and movements that expanded (and contested) the right to vote.",
  remembrance: "Days of remembrance — solemn anniversaries and memorials that ask the country not to look away.",
};

export const CategoryPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const label = CATEGORY_LABELS[slug];
  const description = CATEGORY_DESCRIPTIONS[slug] || `Observances and historic milestones in ${label || "this category"}.`;

  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");

  useDocumentMeta({
    title: label ? `${label} — observances & historic milestones · Will Not Be Forgotten` : "Category · Will Not Be Forgotten",
    description,
    canonical: `${BASE}/category/${slug}`,
  });

  useEffect(() => {
    if (!label) return;
    let alive = true;
    setEntries(null);
    setError("");
    fetchDates({ category: slug, limit: 500 })
      .then((d) => alive && setEntries([...d].sort(sortByDate)))
      .catch(() => alive && setError("We couldn’t load this category right now. Please try again shortly."));
    return () => { alive = false; };
  }, [slug, label]);

  const jsonLd = useMemo(
    () => (label && entries && entries.length ? categoryPageJsonLd(slug, label, description, entries) : null),
    [slug, label, description, entries]
  );
  useJsonLd(jsonLd);

  if (!label) {
    return (
      <Layout headerVariant="solid">
        <section className="section" style={{ paddingTop: "6rem", textAlign: "center" }}>
          <div className="shell" style={{ maxWidth: 560 }}>
            <span className="section__eyebrow">Category not found</span>
            <h1 style={{ marginTop: "0.5rem" }}>That theme isn’t in the archive.</h1>
            <p className="section__lede" style={{ margin: "1rem auto 1.75rem" }}>
              The category you’re looking for doesn’t exist. Browse the full calendar instead.
            </p>
            <Link to="/#explorer" className="btn btn--primary" data-testid="category-notfound-cta">
              Explore the calendar <ArrowRight size={15} aria-hidden />
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  const otherCategories = Object.entries(CATEGORY_LABELS).filter(([s]) => s !== slug);

  return (
    <Layout headerVariant="solid">
      <section data-testid="category-hero" className="section section--no-border" style={{ paddingTop: "5rem", paddingBottom: "2rem" }}>
        <div className="shell">
          <nav aria-label="Breadcrumb" style={{ marginBottom: "1.25rem" }}>
            <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: "0.8rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "hsl(var(--muted))" }}>
              <ArrowLeft size={13} aria-hidden /> The archive
            </Link>
          </nav>
          <span className="section__eyebrow">Browse by theme</span>
          <h1 data-testid="category-title" style={{ fontSize: "clamp(2.3rem, 4.4vw, 4rem)", lineHeight: 1.02, marginBottom: "1rem" }}>
            {label}.
          </h1>
          <p className="section__lede" style={{ marginTop: 0, maxWidth: 760 }}>{description}</p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: "1rem" }}>
        <div className="shell">
          {error && <p role="alert" data-testid="category-error" style={{ color: "hsl(var(--destructive))" }}>{error}</p>}

          {entries === null && !error && (
            <p data-testid="category-loading" style={{ color: "hsl(var(--muted))" }}>Loading entries…</p>
          )}

          {entries !== null && entries.length === 0 && !error && (
            <div data-testid="category-empty" style={{ border: "1px dashed hsl(var(--line-strong))", padding: "2.5rem", textAlign: "center", background: "hsl(var(--bg-elev))" }}>
              <p style={{ margin: 0 }}>No entries are filed under {label} yet — check back soon.</p>
            </div>
          )}

          {entries && entries.length > 0 && (
            <>
              <p data-testid="category-count" style={{ fontSize: "0.85rem", color: "hsl(var(--muted))", marginBottom: "1.5rem" }}>
                {entries.length} {entries.length === 1 ? "entry" : "entries"} in this collection.
              </p>
              <div data-testid="category-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "1.25rem" }}>
                {entries.map((entry) => (
                  <DateCard key={entry.id} entry={entry} onOpen={(e) => navigate(`/date/${e.id}`)} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="section" style={{ background: "hsl(var(--bg-deep))" }}>
        <div className="shell">
          <span className="section__eyebrow">Explore more themes</span>
          <div data-testid="category-other-links" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: "1.25rem" }}>
            {otherCategories.map(([s, l]) => (
              <Link key={s} to={`/category/${s}`} className="tag" data-testid={`category-link-${s}`}
                style={{ textDecoration: "none", padding: "0.45rem 0.85rem", cursor: "pointer" }}>
                {l}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};
